import { useEffect, useMemo, type ReactNode } from 'react';
import { IntlProvider } from 'react-intl';
import {
    messagesByLocale,
    defaultLocale,
    localeValue,
    resolveLocale,
    storeLocale
} from './index';
import { LocaleContext } from './localeContext';
import { useStoredValue } from '../utils/useStoredValue';

/**
 * The browser's preference, asked once: it cannot change while the app runs,
 * and it only matters until the user picks a language of their own.
 */
const browserLocale = resolveLocale();

/**
 * Wraps the app in react-intl's IntlProvider and owns the UI language.
 *
 * The language starts as the user's stored choice, or the browser's preference
 * while they have never made one — so nothing is persisted until they switch.
 *
 * Switching re-renders rather than reloading, so MUI's own component text must
 * follow along: see LocalizedThemeProvider.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
    // The stored choice IS the state, so switching the language in one window
    // takes the others with it (see useStoredValue).
    const locale = useStoredValue(localeValue) ?? browserLocale;

    // The page language drives spell checking and the keyboard's suggestions,
    // so the text fields are checked in the language people write in.
    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    const value = useMemo(
        () => ({
            locale,
            // Writing is all there is to do: the write announces itself, and
            // the read above follows.
            setLocale: storeLocale
        }),
        [locale]
    );

    return (
        <LocaleContext.Provider value={value}>
            <IntlProvider
                locale={locale}
                defaultLocale={defaultLocale}
                messages={messagesByLocale[locale]}
            >
                {children}
            </IntlProvider>
        </LocaleContext.Provider>
    );
}
